import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Users, AlertTriangle, Loader2, ChevronRight, RefreshCw, Mail, Building2, Phone, MapPin, ArrowRight } from 'lucide-react';

/**
 * Settings → Duplicate Clients Finder.
 *
 * Scans the entire client base for duplicates grouped by:
 *   • Email (case-insensitive)
 *   • Surname + phone digits
 *   • Company name (when both clients are companies)
 *
 * Each group renders side-by-side cards so the operator can pick the keeper,
 * compare contact details + linked-record counts, and merge in one click.
 * Merging re-points every property / appraisal / lead / claim onto the
 * keeper and deletes the duplicate client record.
 */
const CRITERION_BADGES = {
  email: { label: 'Same Email', icon: Mail, colour: 'bg-blue-50 text-blue-700 border-blue-200' },
  surname_phone: { label: 'Same Surname + Phone', icon: Phone, colour: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  company: { label: 'Same Company Name', icon: Building2, colour: 'bg-amber-50 text-amber-700 border-amber-200' },
};

export default function DuplicateClientsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyGroup, setBusyGroup] = useState(null);
  const [keeperByGroup, setKeeperByGroup] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/duplicate-clients');
      setData(res.data);
      // Default keeper = client with the highest linked-record count (so the
      // operator's first instinct is to keep the most "alive" record).
      const initialKeeper = {};
      (res.data?.groups || []).forEach((g, i) => {
        const best = [...g.members].sort((a, b) => {
          const ac = (a.counts?.properties || 0) + (a.counts?.appraisals || 0) + (a.counts?.leads || 0);
          const bc = (b.counts?.properties || 0) + (b.counts?.appraisals || 0) + (b.counts?.leads || 0);
          return bc - ac;
        })[0];
        if (best) initialKeeper[`g${i}`] = best._id;
      });
      setKeeperByGroup(initialKeeper);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const mergeMember = async (groupIdx, member) => {
    const keeperId = keeperByGroup[`g${groupIdx}`];
    if (!keeperId) { alert('Pick a keeper first.'); return; }
    if (keeperId === member._id) { alert('That client IS the keeper — pick a different duplicate to merge.'); return; }
    const counts = member.counts || {};
    const total = (counts.properties || 0) + (counts.appraisals || 0) + (counts.leads || 0);
    const memberLabel = member.is_company && member.company_name ? member.company_name : `${member.name_first || ''} ${member.name_last || ''}`.trim() || '(unnamed)';
    if (!window.confirm(`Merge "${memberLabel}" (C${member.access_id}) INTO the keeper?\n\nThis will move ${total} linked record${total === 1 ? '' : 's'} (${counts.properties || 0} propert${(counts.properties || 0) === 1 ? 'y' : 'ies'}, ${counts.appraisals || 0} appraisal${(counts.appraisals || 0) === 1 ? '' : 's'}, ${counts.leads || 0} lead${(counts.leads || 0) === 1 ? '' : 's'}) onto the keeper, then permanently delete this duplicate client record.\n\nCannot be undone.`)) return;

    setBusyGroup(`${groupIdx}:${member._id}`);
    try {
      await api.post('/admin/merge-clients', { keeper_id: keeperId, duplicate_id: member._id });
      // Optimistically remove the merged member, recompute group counts.
      setData(prev => {
        if (!prev) return prev;
        const groups = prev.groups.map((g, i) => {
          if (i !== groupIdx) return g;
          const keeper = g.members.find(m => m._id === keeperId);
          const newMembers = g.members
            .filter(m => m._id !== member._id)
            .map(m => m._id === keeperId && keeper ? {
              ...keeper,
              counts: {
                properties: (keeper.counts?.properties || 0) + (counts.properties || 0),
                appraisals: (keeper.counts?.appraisals || 0) + (counts.appraisals || 0),
                leads: (keeper.counts?.leads || 0) + (counts.leads || 0),
              },
            } : m);
          return { ...g, members: newMembers };
        }).filter(g => g.members.length >= 2);
        return { ...prev, groups, groups_found: groups.length, duplicate_clients: groups.reduce((n, g) => n + g.members.length, 0) };
      });
    } catch (e) {
      alert(e?.response?.data?.detail || 'Merge failed');
    }
    setBusyGroup(null);
  };

  return (
    <div data-testid="duplicate-clients-page" className="space-y-5">
      {/* Header */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-6 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[3px] bg-[#1E40AF]" />
        <div className="flex items-start gap-4 justify-between flex-wrap">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-gradient-to-br from-[#1E40AF]/15 to-[#1E40AF]/5 flex items-center justify-center ring-1 ring-[#1E40AF]/10">
              <Users size={22} className="text-[#1E40AF]" strokeWidth={2} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-0.5">Settings · Data Maintenance</p>
              <h1 className="text-2xl font-extrabold text-slate-900 font-[Manrope] tracking-tight">Duplicate Clients Finder</h1>
              <p className="text-sm text-slate-500 mt-1 max-w-3xl">
                Surface duplicate client records imported from the legacy database. Pick a <strong>keeper</strong> in each group and merge the duplicates into it — every property, appraisal, lead and insurance claim attached to a duplicate is re-pointed at the keeper before the duplicate is permanently deleted.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              data-testid="refresh-dupes"
              onClick={load}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 rounded-md disabled:opacity-50"
            >
              {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Re-scan
            </button>
            <button
              data-testid="back-to-settings"
              onClick={() => navigate('/settings')}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-md"
            >
              Back to Settings <ChevronRight size={12} />
            </button>
          </div>
        </div>

        {data && (
          <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-3">
            <Stat label="Clients Scanned" value={data.scanned_clients} />
            <Stat label="Duplicate Groups" value={data.groups_found} accent={data.groups_found > 0} />
            <Stat label="Records Involved" value={data.duplicate_clients} />
            <Stat label="By Email / Surname-Phone / Company" value={`${(data.groups || []).filter(g => g.criterion === 'email').length} / ${(data.groups || []).filter(g => g.criterion === 'surname_phone').length} / ${(data.groups || []).filter(g => g.criterion === 'company').length}`} compact />
          </div>
        )}
      </div>

      {loading ? (
        <div className="bg-white border border-[#E2E2DF] rounded-md p-12 text-center text-sm text-slate-400">
          <Loader2 size={20} className="animate-spin inline mr-2" /> Scanning {data?.scanned_clients || 1800} clients…
        </div>
      ) : (data?.groups || []).length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-10 text-center">
          <p className="text-base font-semibold text-emerald-800">No duplicate clients found.</p>
          <p className="text-xs text-emerald-700 mt-1">The client base is clean by all three criteria (email / surname+phone / company name).</p>
        </div>
      ) : (
        <div className="space-y-4">
          {data.groups.map((g, idx) => (
            <DuplicateGroupCard
              key={`g${idx}`}
              group={g}
              groupIdx={idx}
              keeperId={keeperByGroup[`g${idx}`]}
              onPickKeeper={id => setKeeperByGroup(prev => ({ ...prev, [`g${idx}`]: id }))}
              onMerge={member => mergeMember(idx, member)}
              busyMember={busyGroup && busyGroup.startsWith(`${idx}:`) ? busyGroup.split(':')[1] : null}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, compact }) {
  return (
    <div className={`rounded-md p-3 border ${accent ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <p className={`mt-0.5 font-extrabold font-[Manrope] font-mono-num ${compact ? 'text-base' : 'text-2xl'} ${accent ? 'text-amber-800' : 'text-slate-800'}`}>{value}</p>
    </div>
  );
}

function DuplicateGroupCard({ group, groupIdx, keeperId, onPickKeeper, onMerge, busyMember }) {
  const badge = CRITERION_BADGES[group.criterion] || CRITERION_BADGES.email;
  const Icon = badge.icon;
  return (
    <div data-testid={`dupe-group-${groupIdx}`} className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E2E2DF] bg-slate-50/40">
        <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-bold rounded-full px-2 py-0.5 border ${badge.colour}`}>
          <Icon size={11} /> {badge.label}
        </span>
        <p className="text-sm font-semibold text-slate-800 truncate flex-1">{group.label}</p>
        <span className="text-[11px] text-slate-500 font-mono">{group.members.length} records</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
        {group.members.map(m => {
          const isKeeper = m._id === keeperId;
          const busy = busyMember === m._id;
          const counts = m.counts || {};
          const total = (counts.properties || 0) + (counts.appraisals || 0) + (counts.leads || 0);
          const displayName = m.is_company && m.company_name ? m.company_name : `${m.name_first || ''} ${m.name_last || ''}`.trim() || '(unnamed)';
          return (
            <div
              key={m._id}
              data-testid={`dupe-member-${m._id}`}
              className={`rounded-md p-3 border transition-all ${isKeeper ? 'border-[#D35400] bg-orange-50/40 ring-2 ring-[#D35400]/15' : 'border-slate-200 bg-white hover:border-slate-300'}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-slate-800 text-sm truncate">{displayName}</p>
                    {m.access_id && <span className="font-mono-num text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded px-1.5 py-px">C{m.access_id}</span>}
                  </div>
                  <p className="text-[11px] text-slate-500 truncate">{m.email || '—'}</p>
                </div>
                <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 cursor-pointer whitespace-nowrap">
                  <input
                    data-testid={`keep-${m._id}`}
                    type="radio"
                    name={`keep-g${groupIdx}`}
                    checked={isKeeper}
                    onChange={() => onPickKeeper(m._id)}
                    className="accent-[#D35400]"
                  />
                  Keep
                </label>
              </div>

              <div className="grid grid-cols-3 gap-1 text-[10px] text-slate-500 mb-2">
                {m.phone_mobile && <div className="truncate"><span className="font-bold uppercase tracking-[0.1em]">M</span> {m.phone_mobile}</div>}
                {m.phone_home && <div className="truncate"><span className="font-bold uppercase tracking-[0.1em]">H</span> {m.phone_home}</div>}
                {m.phone_work && <div className="truncate"><span className="font-bold uppercase tracking-[0.1em]">W</span> {m.phone_work}</div>}
              </div>

              {(m.address_street || m.address_suburb) && (
                <p className="text-[10px] text-slate-500 flex items-center gap-1 mb-2 truncate">
                  <MapPin size={9} className="text-slate-400 flex-shrink-0" />
                  {[m.address_num, m.address_street, m.address_suburb, m.address_city].filter(Boolean).join(' ')}
                </p>
              )}

              <div className="flex items-center gap-2 text-[10px]">
                <span className="bg-slate-100 text-slate-700 rounded px-1.5 py-0.5 font-mono-num">{counts.properties || 0} prop</span>
                <span className="bg-slate-100 text-slate-700 rounded px-1.5 py-0.5 font-mono-num">{counts.appraisals || 0} appr</span>
                <span className="bg-slate-100 text-slate-700 rounded px-1.5 py-0.5 font-mono-num">{counts.leads || 0} lead</span>
                <span className="ml-auto font-mono-num text-slate-500">{total} total</span>
              </div>

              <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-end gap-2">
                {!isKeeper ? (
                  <button
                    data-testid={`merge-${m._id}`}
                    onClick={() => onMerge(m)}
                    disabled={busy || !keeperId}
                    className="flex items-center gap-1 text-[11px] font-bold text-white bg-rose-600 hover:bg-rose-700 px-2.5 py-1 rounded-md disabled:opacity-50"
                  >
                    {busy ? <Loader2 size={11} className="animate-spin" /> : <ArrowRight size={11} />}
                    Merge into keeper
                  </button>
                ) : (
                  <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#D35400]">Keeping</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
